import { useEffect, useState } from 'react';
import { FileText, Download, Loader2, AlertCircle, Plus } from 'lucide-react';
import { apiFetch, downloadFile } from '../lib/api';
import FiscalProfileDialog, { type FiscalProfile } from './FiscalProfileDialog';
import { useTranslation } from 'react-i18next';

/**
 * The CFDI for one invoice: ask for it, or download the one that exists.
 *
 * Kept small and inline rather than a page of its own, because to a customer
 * this is one question — "¿y mi factura?" — asked from wherever they are
 * looking at what they paid.
 */

export interface InvoiceCfdiState {
  cfdiStatus?: 'NOT_REQUIRED' | 'PENDING' | 'STAMPED' | 'FAILED';
  /** What the PAC said. Specific enough to act on, so it is shown. */
  cfdiError?: string | null;
  cfdiUuid?: string | null;
  cfdiSerie?: string | null;
  cfdiFolio?: string | null;
  cfdiXmlUrl?: string | null;
  cfdiPdfUrl?: string | null;
}

export default function InvoiceCfdi({
  invoice,
  paid,
  onRequested,
}: {
  invoice: InvoiceCfdiState & { id: string };
  paid: boolean;
  onRequested?: () => void;
}) {
  const { t } = useTranslation();
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queued, setQueued] = useState(false);
  /** Shown only when there is a choice to make; one identity needs no menu. */
  const [choosing, setChoosing] = useState<FiscalProfile[] | null>(null);
  const [chosenId, setChosenId] = useState<string>('');
  const [addingProfile, setAddingProfile] = useState(false);

  /**
   * While it is being issued, ask again.
   *
   * Stamping happens outside the request, so without this the page sits on
   * "generando tu factura" until somebody reloads — which is how a comprobante
   * that failed two minutes ago still looks like it is being worked on.
   */
  useEffect(() => {
    const stamping = invoice.cfdiStatus === 'PENDING' || queued;
    if (!stamping || !onRequested) return;

    const timer = window.setInterval(() => onRequested(), 5000);
    // Ten minutes of asking is long enough; past that it is not coming back
    // without somebody looking at it.
    const stop = window.setTimeout(() => window.clearInterval(timer), 10 * 60 * 1000);

    return () => {
      window.clearInterval(timer);
      window.clearTimeout(stop);
    };
  }, [invoice.cfdiStatus, queued, onRequested]);

  const stem = `${invoice.cfdiSerie ?? ''}${invoice.cfdiFolio ?? ''}` || invoice.id;

  async function download(kind: 'xml' | 'pdf') {
    setError(null);
    try {
      await downloadFile(`/customer/fiscal/invoices/${invoice.id}/${kind}`, `${stem}.${kind}`);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  const status = invoice.cfdiStatus ?? 'NOT_REQUIRED';

  /** Ask for the comprobante, billed to one identity. */
  async function request(fiscalProfileId?: string) {
    setRequesting(true);
    setError(null);
    try {
      await apiFetch(`/customer/fiscal/invoices/${invoice.id}/request`, {
        method: 'POST',
        body: JSON.stringify(fiscalProfileId ? { fiscalProfileId } : {}),
      });
      setQueued(true);
      setChoosing(null);
      onRequested?.();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setRequesting(false);
    }
  }

  /**
   * What happens when they press the button.
   *
   * None of their fiscal data yet: ask for it here rather than sending them to
   * another screen with an apology. Exactly one identity: nothing to choose,
   * so just ask. Several: they pick, because we cannot know whether this
   * purchase was personal or the company's.
   */
  async function start() {
    setError(null);
    setRequesting(true);
    try {
      const response = await apiFetch<FiscalProfile[]>('/customer/fiscal/profiles');
      const profiles = (response.data as FiscalProfile[]) || [];

      if (profiles.length === 0) {
        setAddingProfile(true);
        return;
      }
      if (profiles.length === 1) {
        await request(profiles[0]!.id);
        return;
      }

      setChosenId(profiles.find((p) => p.isDefault)?.id ?? profiles[0]!.id);
      setChoosing(profiles);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setRequesting(false);
    }
  }

  if (status === 'STAMPED') {
    return (
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="inline-flex items-center gap-1.5 text-green-700">
          <FileText className="h-4 w-4" />
          Factura {invoice.cfdiSerie}
          {invoice.cfdiFolio}
        </span>
        {invoice.cfdiPdfUrl && (
          <button
            className="inline-flex items-center gap-1 text-brand-600 hover:underline"
            onClick={() => download('pdf')}
          >
            <Download className="h-4 w-4" /> PDF
          </button>
        )}
        {invoice.cfdiXmlUrl && (
          <button
            className="inline-flex items-center gap-1 text-brand-600 hover:underline"
            onClick={() => download('xml')}
          >
            <Download className="h-4 w-4" /> XML
          </button>
        )}
        {error && <span className="text-amber-700">{error}</span>}
      </div>
    );
  }

  // Being issued, or waiting to be. Stamping happens out of band, so this is a
  // normal state rather than a delay worth apologising for.
  if (status === 'PENDING' || queued) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t('fiscal.generating')}
      </span>
    );
  }



  // The PAC refused it, which almost always means the fiscal data does not match
  // the constancia. The customer can fix that themselves.
  if (status === 'FAILED') {
    return (
      <div className="text-sm text-amber-700">
        <span className="inline-flex items-center gap-1.5">
          <AlertCircle className="h-4 w-4" />
          {t('fiscal.stampFailed')}
        </span>
        {/* The PAC says which field it disagreed with — an RFC not on the
            SAT's list, a régimen that does not match it. Repeating our guess
            instead of its answer leaves the customer changing things at
            random. */}
        {invoice.cfdiError && <p className="mt-1 text-xs text-gray-600">{invoice.cfdiError}</p>}
      </div>
    );
  }

  if (!paid) return null;

  return (
    <div className="text-sm">
      {choosing ? (
        <div className="rounded-lg border border-gray-200 p-3 space-y-2 max-w-sm">
          <p className="font-medium">{t('fiscal.whichIdentity')}</p>
          <select className="input" value={chosenId} onChange={(e) => setChosenId(e.target.value)}>
            {choosing.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label} — {p.taxId}
              </option>
            ))}
          </select>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => request(chosenId)}
              disabled={requesting || !chosenId}
              className="px-3 py-1.5 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
            >
              {requesting ? 'Solicitando…' : 'Solicitar factura'}
            </button>
            <button
              onClick={() => setAddingProfile(true)}
              className="inline-flex items-center gap-1 text-brand-600 hover:underline"
            >
              <Plus className="h-3.5 w-3.5" /> {t('fiscal.otherDetails')}
            </button>
            <button onClick={() => setChoosing(null)} className="text-gray-500 hover:underline">
              {t('fiscal.cancel')}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={start}
          disabled={requesting}
          className="inline-flex items-center gap-1.5 text-brand-600 hover:underline disabled:opacity-50"
        >
          <FileText className="h-4 w-4" />
          {requesting ? 'Solicitando…' : 'Solicitar factura'}
        </button>
      )}

      {error && (
        <p className="mt-1 flex items-center gap-1 text-amber-700">
          <AlertCircle className="h-3.5 w-3.5" />
          {error}
        </p>
      )}

      {addingProfile && (
        <FiscalProfileDialog
          onClose={() => setAddingProfile(false)}
          onSaved={(profile) => {
            setAddingProfile(false);
            // They filled it in to invoice this purchase, so invoice it.
            void request(profile.id);
          }}
        />
      )}
    </div>
  );
}
